import { inject, Injectable, signal } from '@angular/core';
import { from, map, type Observable, shareReplay, switchMap, tap } from 'rxjs';

import { TECHNICAL_LOG_HANDLER } from '../../../../core/telemetry';
import { ReportInvalidError } from '../../models/report-invalid.error';
import { ReportMapper } from '../../models/report.mapper';
import type { ReportCategory, ReportDraft } from '../../models/report.model';
import { REPORT_MAX_TEXT_LENGTH } from '../../models/report.model';
import { REPORT_PROVIDER } from '../../tokens/reports.tokens';
import type { IReportHandler } from './i-report.handler';

@Injectable()
export class ReportHandler implements IReportHandler {
  private readonly provider = inject(REPORT_PROVIDER);
  private readonly technicalLog = inject(TECHNICAL_LOG_HANDLER);

  private readonly _categories = signal<readonly ReportCategory[]>([]);
  readonly categories = this._categories.asReadonly();

  /**
   * La richiesta in corso, conservata per chi arriva dopo. Il catalogo e' del Motore e non cambia
   * mentre la scheda e' aperta: rileggerlo a ogni apertura della modale sarebbe una chiamata per ogni
   * volta che qualcuno ci ripensa.
   */
  private request?: Observable<readonly ReportCategory[]>;

  loadCategories(): Observable<readonly ReportCategory[]> {
    this.request ??= this.provider.fetchCategories().pipe(
      tap((categories) => this._categories.set(categories)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.request;
  }

  submit(sessionId: string | undefined, draft: ReportDraft): Observable<void> {
    ensureValid(sessionId, draft);

    // Il diario si raccoglie qui e non nella pagina: chi segnala descrive il problema, e cio' che il
    // browser ha registrato lo allega l'applicazione senza chiederglielo.
    return from(this.technicalLog.collectRecent()).pipe(
      map((technicalLogs) => ReportMapper.toDto(draft, technicalLogs)),
      switchMap((dto) => this.provider.submit(sessionId, dto)),
    );
  }
}

function ensureValid(sessionId: string | undefined, draft: ReportDraft): void {
  // Non nominare una conversazione e nominarla vuota sono due cose diverse: la prima e' la
  // segnalazione che parte fuori da una chat, la seconda e' un difetto di chi chiama.
  if (sessionId !== undefined && !sessionId) {
    throw new ReportInvalidError('it names an empty conversation');
  }
  if (!draft.category) throw new ReportInvalidError('no category was chosen');
  if (!draft.text.trim()) throw new ReportInvalidError('the description is empty');
  if (draft.text.length > REPORT_MAX_TEXT_LENGTH) {
    throw new ReportInvalidError(
      `the description is longer than ${REPORT_MAX_TEXT_LENGTH} characters`,
    );
  }
}
